import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

// Import memory photos
import memory1 from '../../assets/memories/that-night.png';
import memory2 from '../../assets/memories/second-date.png';
import memory3 from '../../assets/memories/portola-babies.png';

const IntroSection: React.FC = () => {
  const [showFullContent, setShowFullContent] = useState(false);
  const [daysCount, setDaysCount] = useState(0);
  const navigate = useNavigate();
  
  useEffect(() => {
    // Calculate days between the start of relationship and now
    const startDate = new Date('2023-06-14');
    const currentDate = new Date();
    const timeDifference = currentDate.getTime() - startDate.getTime();
    const daysDifference = Math.floor(timeDifference / (1000 * 3600 * 24));
    
    setDaysCount(daysDifference);
    
    // Show full content after initial animation
    const timer = setTimeout(() => {
      setShowFullContent(true);
    }, 2500);
    
    return () => clearTimeout(timer);
  }, []);
  
  // Animation for text reveal
  const textVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8 } }
  };
  
  // Animation for the whole intro container
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        duration: 1,
        when: "beforeChildren",
        staggerChildren: 0.3
      } 
    }
  };
  
  // Animation for the days counter
  const counterVariants = {
    initial: { scale: 0.8, opacity: 0 },
    animate: { 
      scale: [0.8, 1.2, 1],
      opacity: 1,
      transition: { duration: 1, times: [0, 0.7, 1] }
    }
  };
  
  // Animation for the polaroid photos
  const polaroidVariants = {
    hidden: { opacity: 0, scale: 0.8, rotate: -10 },
    visible: (i: number) => ({
      opacity: 1,
      scale: 1,
      rotate: [-10, 10, 0][i % 3],
      transition: { 
        duration: 0.6,
        delay: 0.3 + (i * 0.2)
      }
    })
  };
  
  const navigateToMemories = () => {
    navigate('/our-memories');
  };

  // Memory photos data with imported images
  const memoryPhotos = [
    { id: 1, src: memory1, caption: 'That Night 💕' },
    { id: 2, src: memory2, caption: 'Second Date 🌟' },
    { id: 3, src: memory3, caption: 'Portola Babies ✨' }
  ];

  return (
    <StyledIntroSection id="intro">
      <IntroBackground>
        {/* Decorative elements for K-pop style background */}
        <BackgroundCircle top="10%" left="10%" size="15vw" color="primary" delay={0.3} />
        <BackgroundCircle top="70%" left="80%" size="10vw" color="secondary" delay={0.5} />
        <BackgroundCircle top="40%" left="90%" size="5vw" color="accent1" delay={0.8} />
      </IntroBackground>
      
      <IntroContent
        as={motion.div}
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.h1 variants={textVariants}>
          Our 2nd Anniversary
        </motion.h1>
        
        <DateContainer variants={textVariants}>
          <span>June 14, 2023</span>
          <HeartIcon>♥</HeartIcon>
          <span>June 14, 2025</span>
        </DateContainer>
        
        <AnimatePresence>
          {showFullContent && (
            <DaysCounter
              variants={counterVariants}
              initial="initial"
              animate="animate"
              exit={{ opacity: 0, scale: 0.8 }}
            >
              <span>{daysCount}</span> days of love
            </DaysCounter>
          )}
        </AnimatePresence>
        
        {/* K-pop style polaroid photos */}
        <AnimatePresence>
          {showFullContent && (
            <PolaroidContainer>
              {memoryPhotos.map((memory, index) => (
                <Polaroid
                  key={memory.id}
                  custom={index}
                  variants={polaroidVariants}
                  initial="hidden"
                  animate="visible"
                >
                  <div className="polaroid-inner">
                    <img 
                      src={memory.src} 
                      alt={memory.caption}
                      className="polaroid-img"
                      onError={(e) => {
                        // Fallback if image doesn't load
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        target.nextElementSibling?.classList.add('fallback-visible');
                      }}
                    />
                    <div className="polaroid-img-fallback">
                      📸 Add Photo
                    </div>
                    <div className="polaroid-caption">{memory.caption}</div>
                  </div>
                </Polaroid>
              ))}
            </PolaroidContainer>
          )}
        </AnimatePresence>
        
        <AnimatePresence>
          {showFullContent && (
            <ExploreButton
              onClick={navigateToMemories}
              variants={textVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              Explore Our Memories
            </ExploreButton>
          )}
        </AnimatePresence>
      </IntroContent>
    </StyledIntroSection>
  );
};

const StyledIntroSection = styled.section`
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  text-align: center;
  background-color: ${({ theme }) => theme.colors.background};
`;

const IntroBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 1;
`;

const BackgroundCircle = styled(motion.div)<{ 
  top: string;
  left: string;
  size: string;
  color: string;
  delay: number;
}>`
  position: absolute;
  top: ${props => props.top};
  left: ${props => props.left};
  width: ${props => props.size};
  height: ${props => props.size};
  border-radius: 50%;
  background: ${props => 
    // Use type assertion to tell TypeScript this is a valid color key
    props.theme.colors[props.color as keyof typeof props.theme.colors] || props.theme.colors.primary};
  opacity: 0.15;
  filter: blur(30px);
  animation: pulse 8s infinite alternate-reverse;
  animation-delay: ${props => props.delay}s;
  
  @keyframes pulse {
    0% {
      transform: scale(1);
      opacity: 0.15;
    }
    50% {
      transform: scale(1.2);
      opacity: 0.2;
    }
    100% {
      transform: scale(1);
      opacity: 0.15;
    }
  }
`;

const IntroContent = styled(motion.div)`
  position: relative;
  z-index: 2;
  width: 90%;
  max-width: 1000px;
  padding: 2rem;
`;

const DateContainer = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin: 1.5rem 0;
  font-size: 1.5rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1.2rem;
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const HeartIcon = styled.span`
  color: ${({ theme }) => theme.colors.primary};
  display: inline-block;
  animation: heartbeat 1.5s infinite;
  
  @keyframes heartbeat {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.3);
    }
    100% {
      transform: scale(1);
    }
  }
`;

const DaysCounter = styled(motion.div)`
  font-size: 1.8rem;
  margin: 2rem 0;
  font-weight: 600;
  
  span {
    font-size: 3rem;
    background: ${({ theme }) => theme.gradients.secondary};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    font-weight: 700;
  }
`;

const PolaroidContainer = styled.div`
  display: flex;
  gap: 1.5rem;
  margin: 3rem 0;
  justify-content: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    gap: 0.5rem;
  }
`;

const Polaroid = styled(motion.div)`
  width: 150px;
  height: 180px;
  background: white;
  border-radius: 4px;
  padding: 10px 10px 30px 10px;
  box-shadow: ${({ theme }) => theme.shadows.medium};
  
  .polaroid-inner {
    width: 100%;
    height: 100%;
    position: relative;
  }
  
  .polaroid-img {
    width: 100%;
    height: calc(100% - 10px);
    object-fit: cover;
    border-radius: 2px;
    display: block;
  }
  
  .polaroid-img-fallback {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: calc(100% - 10px);
    background: linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #999;
    font-size: 1rem;
    border-radius: 2px;
    opacity: 0;
    transition: opacity 0.3s ease;
    
    &.fallback-visible {
      opacity: 1;
    }
  }
  
  .polaroid-caption {
    position: absolute;
    bottom: -25px;
    left: 0;
    right: 0;
    text-align: center;
    font-family: ${({ theme }) => theme.fonts.korean};
    font-weight: 500;
    color: #333;
    font-size: 0.9rem;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 100px;
    height: 120px;
    padding: 8px 8px 25px 8px;
    
    .polaroid-caption {
      font-size: 0.75rem;
      bottom: -22px;
      line-height: 1.2;
      font-weight: 600;
    }
  }
`;

const ExploreButton = styled(motion.button)`
  background: ${({ theme }) => theme.gradients.primary};
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-size: 1.2rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: ${({ theme }) => theme.shadows.medium};
  margin-top: 2rem;
`;

export default IntroSection; 